import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptions } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
import React, { Component } from "react";
import { Field, FieldProps, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import {
  addTariffType,
  editTariffType,
  getAllBasicTariffTypes,
  getAllTarmedTariffTypes
} from 'services/tariffServices';
import styled from 'styled-components';
import { Diagnosis, TariffType } from 'ts-types/api.enums';
import { UpsertTariffDto } from 'ts-types/api.types';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';

const StyledGridBillRow = styled(Grid.Row)`
  margin-bottom: 1rem !important;
`;

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  tariff?: UpsertTariffDto
  tariffId?: number,
}

interface State {
  tariff?: Partial<UpsertTariffDto>,
  errorMessage?: string,
  tarmedTypes?: Array<DropdownOption>,
  basicTariffs?: Array<DropdownOption>,
  tarmedTariffs?: Array<DropdownOption>,
  diagnosis?: Array<RadioProps>,
  cancelTokenSource: CancelTokenSource
}


const VALIDATED_FIELDS: Array<string> = [
  "description",
  "descriptionFr",
  "descriptionIt",
  "descriptionEn"
];

const initialTariffType: Partial<UpsertTariffDto> = {
  tarmedTariff: false,
  active: true,
  type: TariffType.KVG,
  billPeriod: 0,
  diagnosisCode: Diagnosis.CLEAR_TEXT,
  lnkTrfId: -1,
  lnk: false,
  docPrinted: true,
  qrCode: false
};

const Condition = ({when, is, children}: any) => (
    <Field name={when} subscription={{value: true}}>
      {({input: {value}}) => (value === is ? children : null)}
    </Field>
);

class UpsertTariffTypeForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, tariff, tariffId} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const tarmedTypes: Array<DropdownOption> = Object.values(TariffType).map((value: string) => {
      return {
        key: value,
        text: getMessage(`tt.${value.toLowerCase()}`),
        value: value
      }
    });

    const diagnosisLabels: Array<string> = ["clearText", "coded", "suppressed"];
    const diagnosis = generateRadioButtonOptions(diagnosisLabels, "diagnosis", getMessage, "diagnosis", Object.values(Diagnosis));

    this.state = {
      tariff: tariff ? tariff : initialTariffType,
      tarmedTypes: tarmedTypes,
      basicTariffs: [],
      tarmedTariffs: [],
      diagnosis: diagnosis,
      cancelTokenSource
    };

    Promise.all([getAllBasicTariffTypes(cancelTokenSource), getAllTarmedTariffTypes(cancelTokenSource)])
        .then(responses => {
          responses[0].unshift({
            id: -1,
            description: "",
            lnkTrfIdDescription: "",
            billPeriod: 0,
            lnkTrfId: -1,
            tarmedTariff: false,
            type: "",
            typeCode: "",
            baseTariff: false,
            active: true
          });

          const basicTariffs = responses[0].filter((basic) => {
            if (basic.id != tariffId) {
              return basic;
            }
          });

          responses[1].unshift({
            id: -1,
            description: "",
            lnkTrfIdDescription: "",
            billPeriod: 0,
            lnkTrfId: -1,
            tarmedTariff: false,
            type: "",
            typeCode: "",
            baseTariff: false,
            active: true
          });
          const tarmedTariffs = responses[1].filter((basic) => {
            if (basic.id != tariffId) {
              return basic;
            }
          });

          this.setState({
            basicTariffs: mapToDropdownOptionArray(basicTariffs, "description", "id"),
            tarmedTariffs: mapToDropdownOptionArray(tarmedTariffs, "description", "id")
          })
        }).catch(noop);
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  handleSubmit = async (values: Partial<UpsertTariffDto>) => {

    const {tariffId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    let newValues = {...values};
    if (values.lnkTrfId === -1) {
      newValues.lnkTrfId = undefined;
    }

    if (tariffId) {
      await editTariffType(tariffId, newValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addTariffType(newValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  validator = (value: string) => required(value);

  composeValidators = (...validators: any) => (value: any) =>
      validators.reduce((error: any, validator: any) => error || validator(value), undefined);


  tariffConnection = createDecorator({
    field: 'lnkTrfId',
    updates: {
      lnk: (fieldValue, allValues) => {
        //@ts-ignore
        return (fieldValue == -1 || fieldValue == null) ? allValues.lnk = false : allValues.lnk = true;
      }
    }
  });

  linkedTariff = createDecorator({
    field: 'lnk',
    updates: {
      lnkTrfId: (fieldValue, allValues) => {
        //@ts-ignore
        return fieldValue ? allValues.lnkTrfId : allValues.lnkTrfId = null;
      }
    }
  });

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {tariff} = this.state;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={tariff}
            mutators={{
              ...arrayMutators
            }}
            decorators={[
              this.tariffConnection,
              this.linkedTariff
            ]}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderDescriptionSection()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderTariffConnectionSegment(values)}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderBillOptionsHeader()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={6}>
                    <StyledGridBillRow>
                      {this.renderBillPeriod()}
                    </StyledGridBillRow>
                    <Grid.Row>
                      {this.renderDiagnosisSegment()}
                    </Grid.Row>
                  </Grid.Column>
                  <Grid.Column width={5}>
                    {this.renderPrintoutSegment()}
                  </Grid.Column>
                  <Grid.Column width={5}>
                    {this.renderCalendariumSegment()}
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderBillOptionsHeader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("guarantor.billingOptions")}
              </Header>
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    )

  }

  renderDescriptionSection = (): React.ReactNode => {

    const {getMessage, tariff} = this.props;
    const {tarmedTypes} = this.state;

    const selectMap: IndexSignature = {
      type: tarmedTypes
    };


    const descriptionRows: Array<Array<string>> = [
      ["description"],
      ["descriptionFr"],
      ["descriptionIt"],
      ["descriptionEn"]
    ];

    const descriptionSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? this.validator : undefined,
                  component: Input
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">
                        {getMessage(field)}
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("description")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {descriptionSection}
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="baseTariff"
                  component={CheckBox}
                  label={getMessage("tariff.basic")}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tarmedTariff"
                  component={CheckBox}
                  disabled={tariff != undefined}
                  label={getMessage("tariff.tarmed")}
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Field
                  name="active"
                  component={CheckBox}
                  label={getMessage("tariff.active")}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
              {getMessage("tariff.tarmed.type")}
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="type"
                  component={Select}
                  fluid
                  search
                  options={selectMap["type"]}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderTariffsDropdown = () => {
    const {basicTariffs, tarmedTariffs} = this.state;

    const basicSelectMap: IndexSignature = {
      lnkTrfId: basicTariffs
    };
    const tarmedSelectMap: IndexSignature = {
      lnkTrfId: tarmedTariffs
    };

    return (
        <React.Fragment>
          <Condition when="tarmedTariff" is={false}>
            <Grid.Column width={6}>
              <Field
                  name="lnkTrfId"
                  component={Select}
                  fluid
                  search
                  options={basicSelectMap["lnkTrfId"]}
              />
            </Grid.Column>
          </Condition>
          <Condition when="tarmedTariff" is={true}>
            <Grid.Column width={6}>
              <Field
                  name="lnkTrfId"
                  component={Select}
                  fluid
                  search
                  options={tarmedSelectMap["lnkTrfId"]}
              />
            </Grid.Column>
          </Condition>
        </React.Fragment>
    );
  };

  renderTariffConnectionSegment = (values: any): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("tariff.connection")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
            </Grid.Column>
            <Grid.Column width={7}>
              {this.renderTariffsDropdown()}
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="lnk"
                  component={CheckBox}
                  disabledWhenFalse={(values.lnkTrfId == -1 || values.lnkTrfId == null)}
                  label={getMessage("tariff.connection")}
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderBillPeriod = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("tariff.bill.after")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={5}>
              <Field
                  name="billPeriod"
                  component={Input}
                  onKeyDown={ensureOnlyNumericInput}
                  validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage("days")}
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderPrintoutSegment = (): React.ReactNode => {
    const {getMessage} = this.props;

    const descriptionRows: Array<Array<string>> = [
      ["billText"],
      ["withoutEsr"],
      ["docPrinted"],
      ["qrCode"]
    ];

    const printoutSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  component: CheckBox,
                  label: (getMessage(`tariff.${field}`))
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column verticalAlign="middle">
                        <Field
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("tariff.printout")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {printoutSection}
        </PraxFormGrid>
    );
  };

  renderDiagnosisSegment = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {diagnosis} = this.state;

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage("diagnosis")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Field
                  name="diagnosisCode"
                  component={RadioGroup}
                  radioDefinitions={diagnosis}
                  radiowidth="31%"
              />
            </Grid.Column>
          </Grid.Row>
        </PraxFormGrid>
    );
  };

  renderCalendariumSegment = (): React.ReactNode => {
    const {getMessage} = this.props;

    const descriptionRows: Array<Array<string>> = [
      ["haveProcedureGroups"],
      ["haveProcedure"],
      ["haveMedicament"]
    ];

    const calendariumSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  component: CheckBox,
                  label: (getMessage(`tariff.${field}`))
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column verticalAlign="middle">
                        <Field
                            {...fieldProps}
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header as="h5">
                {getMessage(('tariff.calendarium'))}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {calendariumSection}
        </PraxFormGrid>
    );
  };


}

export default withMessages(UpsertTariffTypeForm);
